import { ButtonType, ButtonSignal, AxisType, ControllerButtonMessage, ControllerAxisMessage, MessageFactory, calcLocalizationStringByteLength, ControllerHand, UID_SAFE_BYTES_PER_ELEMENT, UID_BYTES_PER_ELEMENT, BusMessage, BOOLEAN_BYTES_PER_ELEMENT } from "flavr-common";
export { ButtonType, ButtonSignal, AxisType, ControllerButtonMessage, ControllerAxisMessage };
/**
 * Announces that the local mouse input device was connected and assigned a model UID.
 */
export class MouseConnectedMessage extends BusMessage {
    get byteLength() {
        return MouseConnectedMessage.MAX_BYTE_LENGTH;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === MouseConnectedMessage.typestatic;
    }
}
MouseConnectedMessage.typestatic = "MouseConnectedMessage";
MouseConnectedMessage.subjectstatic = "general";
MouseConnectedMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
MessageFactory.register(MouseConnectedMessage);
/**
 * Announces that the local mouse input device was disconnected.
 */
export class MouseDisconnectedMessage extends BusMessage {
    get byteLength() {
        return MouseDisconnectedMessage.MAX_BYTE_LENGTH;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === MouseDisconnectedMessage.typestatic;
    }
}
MouseDisconnectedMessage.typestatic = "MouseDisconnectedMessage";
MouseDisconnectedMessage.subjectstatic = "general";
MouseDisconnectedMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
MessageFactory.register(MouseDisconnectedMessage);
/**
 * Announces that a controller input device connected and provides its initial ray and hand-tracking state.
 */
export class ControllerConnectedMessage extends BusMessage {
    get byteLength() {
        return ControllerConnectedMessage.MAX_BYTE_LENGTH;
    }
    constructor(uid, controllerType, hand = ControllerHand.Unknown, rayOrigin = [0, 0, 0], rayDirection = [0, 0, -1], hasTrackedHand = false) {
        super();
        this.uid = uid;
        this.controllerType = controllerType;
        this.hand = hand;
        this.rayOrigin = rayOrigin;
        this.rayDirection = rayDirection;
        this.hasTrackedHand = hasTrackedHand;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.controllerType = stream.uint8(this.controllerType);
        this.hand = stream.uint8(this.hand);
        this.rayOrigin = stream.number3(this.rayOrigin);
        this.rayDirection = stream.number3(this.rayDirection);
        this.hasTrackedHand = stream.boolean(this.hasTrackedHand);
    }
    static is(msg) {
        return msg.type === ControllerConnectedMessage.typestatic;
    }
}
ControllerConnectedMessage.typestatic = "ControllerConnectedMessage";
ControllerConnectedMessage.subjectstatic = "controller";
ControllerConnectedMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    Uint8Array.BYTES_PER_ELEMENT + // controllerType
    Uint8Array.BYTES_PER_ELEMENT + // hand
    Float32Array.BYTES_PER_ELEMENT * 3 + // rayOrigin
    Float32Array.BYTES_PER_ELEMENT * 3 + // rayDirection
    BOOLEAN_BYTES_PER_ELEMENT; // hasTrackedHand
MessageFactory.register(ControllerConnectedMessage);
/**
 * Sent when the local controller switches between physical-controller mode and hand-tracking mode.
 * Used by PeerManager to keep ControllerPeer.hasTrackedHand in sync.
 */
export class ControllerTrackingChangedMessage extends BusMessage {
    get byteLength() {
        return ControllerTrackingChangedMessage.MAX_BYTE_LENGTH;
    }
    constructor(uid = 0, hasTrackedHand = false) {
        super();
        this.uid = uid;
        this.hasTrackedHand = hasTrackedHand;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.hasTrackedHand = stream.boolean(this.hasTrackedHand);
    }
    static is(msg) {
        return msg.type === ControllerTrackingChangedMessage.typestatic;
    }
}
ControllerTrackingChangedMessage.typestatic = "ControllerTrackingChangedMessage";
ControllerTrackingChangedMessage.subjectstatic = "controller";
ControllerTrackingChangedMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + BOOLEAN_BYTES_PER_ELEMENT;
MessageFactory.register(ControllerTrackingChangedMessage);
/**
 * Announces that a controller input device disconnected.
 */
export class ControllerDisconnectedMessage extends BusMessage {
    get byteLength() {
        return ControllerDisconnectedMessage.MAX_BYTE_LENGTH;
    }
    constructor(uid = 0) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === ControllerDisconnectedMessage.typestatic;
    }
}
ControllerDisconnectedMessage.typestatic = "ControllerDisconnectedMessage";
ControllerDisconnectedMessage.subjectstatic = "controller";
ControllerDisconnectedMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT; // uid
MessageFactory.register(ControllerDisconnectedMessage);
/**
 * Updates the current pose and linear velocity of a controller model.
 * For tracked hands, `visualTransform` carries the wrist pose for remote rendering.
 */
export class ControllerMovedMessage extends BusMessage {
    get byteLength() {
        return (ControllerMovedMessage.BASE_BYTE_LENGTH +
            BOOLEAN_BYTES_PER_ELEMENT +
            (this.visualTransform === undefined ? 0 : Float32Array.BYTES_PER_ELEMENT * 7));
    }
    constructor(uid = 0, position = [0, 0, 0], orientation = [0, 0, 0, 1], scale = 1, linearVelocity = [0, 0, 0], visualTransform) {
        super();
        this.uid = uid;
        this.position = position;
        this.orientation = orientation;
        this.scale = scale;
        this.linearVelocity = linearVelocity;
        this.visualTransform = visualTransform;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.position = stream.number3(this.position);
        this.orientation = stream.number4(this.orientation);
        this.scale = stream.float32(this.scale);
        this.linearVelocity = stream.number3(this.linearVelocity);
        const hasVisualTransform = stream.boolean(this.visualTransform !== undefined);
        if (stream.isReading) {
            this.visualTransform = hasVisualTransform
                ? { position: [0, 0, 0], orientation: [0, 0, 0, 1] }
                : undefined;
        }
        if (this.visualTransform !== undefined) {
            this.visualTransform.position = stream.number3(this.visualTransform.position);
            this.visualTransform.orientation = stream.number4(this.visualTransform.orientation);
        }
    }
    static is(msg) {
        return msg.type === ControllerMovedMessage.typestatic;
    }
}
ControllerMovedMessage.typestatic = "ControllerMovedMessage";
ControllerMovedMessage.subjectstatic = "controller";
ControllerMovedMessage.BASE_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    Float32Array.BYTES_PER_ELEMENT * 3 + // position
    Float32Array.BYTES_PER_ELEMENT * 4 + // orientation
    Float32Array.BYTES_PER_ELEMENT + // scale
    Float32Array.BYTES_PER_ELEMENT * 3; // linearVelocity
ControllerMovedMessage.MAX_BYTE_LENGTH = ControllerMovedMessage.BASE_BYTE_LENGTH +
    BOOLEAN_BYTES_PER_ELEMENT + // hasVisualTransform
    Float32Array.BYTES_PER_ELEMENT * 7; // visual position + orientation
MessageFactory.register(ControllerMovedMessage);
/**
 * Toggles whether controller meshes should be visible.
 */
export class ControllerVisibilityMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT;
    }
    constructor(visible) {
        super();
        this.visible = visible;
    }
    serialization(stream) {
        this.visible = stream.boolean(this.visible);
    }
    static is(msg) {
        return msg.type === ControllerVisibilityMessage.typestatic;
    }
}
ControllerVisibilityMessage.typestatic = "ControllerVisibilityMessage";
ControllerVisibilityMessage.subjectstatic = "controller";
MessageFactory.register(ControllerVisibilityMessage);
/**
 * Changes the visibility of the controller's selection ray
 */
export class ControllerSelectionRayVisibilityMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT + UID_SAFE_BYTES_PER_ELEMENT;
    }
    constructor(visible, controllerUid) {
        super();
        this.visible = visible;
        this.controllerUid = controllerUid;
    }
    serialization(stream) {
        this.visible = stream.boolean(this.visible);
        this.controllerUid = stream.uidSafe(this.controllerUid);
    }
    static is(msg) {
        return msg.type === ControllerSelectionRayVisibilityMessage.typestatic;
    }
}
ControllerSelectionRayVisibilityMessage.typestatic = "ControllerSelectionRayVisibilityMessage";
ControllerSelectionRayVisibilityMessage.subjectstatic = "controller";
MessageFactory.register(ControllerSelectionRayVisibilityMessage);
/**
 * Triggers haptic feedback on the addressed controller actuator.
 */
export class ControllerHapticFeedback extends BusMessage {
    get byteLength() {
        return ControllerHapticFeedback.MAX_BYTE_LENGTH;
    }
    constructor(uid = 0, actuatorId = 0, pulseValue = 0, pulseDuration = 0) {
        super();
        this.uid = uid;
        this.actuatorId = actuatorId;
        this.pulseValue = pulseValue;
        this.pulseDuration = pulseDuration;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.actuatorId = stream.int32(this.actuatorId);
        this.pulseValue = stream.float32(this.pulseValue);
        this.pulseDuration = stream.float32(this.pulseDuration);
    }
    static is(msg) {
        return msg.type === ControllerHapticFeedback.typestatic;
    }
}
ControllerHapticFeedback.typestatic = "ControllerHapticFeedback";
ControllerHapticFeedback.subjectstatic = "controller";
ControllerHapticFeedback.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    Int32Array.BYTES_PER_ELEMENT + // actuatorId
    Float32Array.BYTES_PER_ELEMENT + // pulseValue
    Float32Array.BYTES_PER_ELEMENT; // pulseDuration
MessageFactory.register(ControllerHapticFeedback);
/**
 * Sets the localized description shown for a controller button.
 */
export class SetControllerButtonDescription extends BusMessage {
    get byteLength() {
        return SetControllerButtonDescription.MAX_BYTE_LENGTH + calcLocalizationStringByteLength(this.description);
    }
    constructor(buttonType = ButtonType.CLICK, description = "", controllerUid) {
        super();
        this.buttonType = buttonType;
        this.description = description;
        this.controllerUid = controllerUid;
    }
    serialization(stream) {
        this.buttonType = stream.uint8(this.buttonType);
        this.description = stream.localizationString(this.description);
        this.controllerUid = stream.uidSafe(this.controllerUid);
    }
    static is(msg) {
        return msg.type === SetControllerButtonDescription.typestatic;
    }
}
SetControllerButtonDescription.typestatic = "SetControllerButtonDescription";
SetControllerButtonDescription.subjectstatic = "controller";
SetControllerButtonDescription.MAX_BYTE_LENGTH = Uint8Array.BYTES_PER_ELEMENT + // buttonType
    UID_SAFE_BYTES_PER_ELEMENT;
MessageFactory.register(SetControllerButtonDescription);
/**
 * Resets a controller button description back to its default value.
 */
export class ResetControllerButtonDescription extends BusMessage {
    get byteLength() {
        return ResetControllerButtonDescription.MAX_BYTE_LENGTH;
    }
    constructor(controllerUid = 0, buttonType = ButtonType.CLICK) {
        super();
        this.controllerUid = controllerUid;
        this.buttonType = buttonType;
    }
    serialization(stream) {
        this.controllerUid = stream.uid(this.controllerUid);
        this.buttonType = stream.uint8(this.buttonType);
    }
    static is(msg) {
        return msg.type === ResetControllerButtonDescription.typestatic;
    }
}
ResetControllerButtonDescription.typestatic = "ResetControllerButtonDescription";
ResetControllerButtonDescription.subjectstatic = "controller";
ResetControllerButtonDescription.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // controllerUid
    Uint8Array.BYTES_PER_ELEMENT; // buttonType
MessageFactory.register(ResetControllerButtonDescription);
/**
 * Updates the audio volume value that should be reflected by controller-related UI or feedback.
 */
export class AudioVolumeMessage extends BusMessage {
    get byteLength() {
        return AudioVolumeMessage.MAX_BYTE_LENGTH;
    }
    constructor(volume = 0) {
        super();
        this.volume = volume;
    }
    serialization(stream) {
        this.volume = stream.float32(this.volume);
    }
    static is(msg) {
        return msg.type === AudioVolumeMessage.typestatic;
    }
}
AudioVolumeMessage.typestatic = "AudioVolumeMessage";
AudioVolumeMessage.subjectstatic = "controller";
AudioVolumeMessage.MAX_BYTE_LENGTH = Float32Array.BYTES_PER_ELEMENT; // volume
MessageFactory.register(AudioVolumeMessage);
